import { useEffect, useState } from "react";
import { api, fmtTime, type Customer, type CustomerWebapp } from "../api";

export function CustomersTab() {
  const [items, setItems] = useState<Customer[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [openWebapps, setOpenWebapps] = useState<Customer | null>(null);

  async function reload() {
    try {
      const r = await api.listCustomers();
      setItems(r.items);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "load_failed");
    }
  }
  useEffect(() => {
    reload();
  }, []);

  async function remove(cu: Customer) {
    if (!confirm(`Delete customer ${cu.email}? This cannot be undone.`)) return;
    try {
      await api.deleteCustomer(cu.id);
      reload();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "delete_failed";
      alert(msg === "has_webapps" ? "Customer still owns webapps — delete those first." : msg);
    }
  }

  return (
    <>
      <div className="panel">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2>Customers</h2>
        </div>
        {err && <div className="error">{err}</div>}
        {items === null ? (
          <div className="muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="muted">No customers yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Webapps</th>
                  <th>Credits</th>
                  <th>Joined</th>
                  <th>Last login</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((cu) => {
                  const low = cu.credits_remaining <= 0;
                  return (
                    <tr key={cu.id}>
                      <td data-label="Email">
                        {cu.email}
                        {!cu.email_verified && (
                          <>
                            {" "}
                            <span className="pill bad">unverified</span>
                          </>
                        )}
                      </td>
                      <td data-label="Phone">
                        {cu.phone ? <code>{cu.phone}</code> : <span className="muted">—</span>}
                      </td>
                      <td data-label="Webapps">{cu.webapp_count}</td>
                      <td data-label="Credits">
                        <code>
                          {cu.consumed_credits} / {cu.credit_allowance}
                        </code>
                        <br />
                        <span className={`pill ${low ? "bad" : "ok"}`}>
                          {cu.credits_remaining} left
                        </span>
                      </td>
                      <td data-label="Joined">{fmtTime(cu.created_at)}</td>
                      <td data-label="Last login">{fmtTime(cu.last_login_at)}</td>
                      <td className="actions" data-label="Actions">
                        <div className="action-group">
                          <button className="secondary" onClick={() => setOpenWebapps(cu)}>
                            Webapps
                          </button>
                          <button className="secondary" onClick={() => setEditing(cu)}>
                            Credits
                          </button>
                          <button className="danger" onClick={() => remove(cu)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <EditCustomerModal
          customer={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      {openWebapps && (
        <CustomerWebappsModal customer={openWebapps} onClose={() => setOpenWebapps(null)} />
      )}
    </>
  );
}

function EditCustomerModal({
  customer,
  onCancel,
  onSaved,
}: {
  customer: Customer;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [trial, setTrial] = useState(String(customer.trial_credits));
  const [paid, setPaid] = useState(String(customer.paid_credits));
  const [verified, setVerified] = useState(customer.email_verified);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.updateCustomer(customer.id, {
        trial_credits: Number(trial) || 0,
        paid_credits: Number(paid) || 0,
        email_verified: verified,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "update_failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Credits — {customer.email}</h3>
        <p className="muted">
          Spent {customer.consumed_credits} of {customer.credit_allowance} granted
          ({customer.credits_remaining} remaining). One verified login = one credit.
        </p>
        <label>
          Trial credits <span className="muted">(one-time free allowance)</span>
        </label>
        <input type="number" min={0} value={trial} onChange={(e) => setTrial(e.target.value)} />
        <label>
          Paid credits <span className="muted">(purchased + manual top-ups)</span>
        </label>
        <input type="number" min={0} value={paid} onChange={(e) => setPaid(e.target.value)} />
        <label className="row" style={{ gap: 8, alignItems: "center", marginTop: 14 }}>
          <input
            type="checkbox"
            checked={verified}
            onChange={(e) => setVerified(e.target.checked)}
            style={{ width: "auto" }}
          />
          <span>Email verified</span>
        </label>
        {err && <div className="error">{err}</div>}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" className="secondary" onClick={onCancel}>
              Cancel
            </button>{" "}
            <button type="submit" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function CustomerWebappsModal({
  customer,
  onClose,
}: {
  customer: Customer;
  onClose: () => void;
}) {
  const [items, setItems] = useState<CustomerWebapp[] | null>(null);
  useEffect(() => {
    api.customerWebapps(customer.id).then((r) => setItems(r.items));
  }, [customer.id]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        style={{ width: "min(760px, 96vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{customer.email} — webapps</h3>
        {items === null ? (
          <div className="muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="muted">No webapps.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Verified</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((w) => (
                  <tr key={w.id}>
                    <td data-label="Code">
                      <code>{w.code}</code>
                    </td>
                    <td data-label="Name">{w.name}</td>
                    <td data-label="Status">
                      {w.deleted ? (
                        <span className="pill bad">deleted</span>
                      ) : (
                        <span className={`pill ${w.active ? "ok" : "bad"}`}>
                          {w.active ? "active" : "disabled"}
                        </span>
                      )}
                    </td>
                    <td data-label="Verified">
                      <code>
                        {w.verified_count}
                        {w.unlimited ? " / ∞" : ""}
                      </code>
                    </td>
                    <td data-label="Created">{fmtTime(w.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <div style={{ flex: "0 0 auto" }}>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
